import React, { useMemo, useState, Children } from 'react';
import { motion } from 'framer-motion';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 16
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};
function formatEuro(value: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
}
interface SliderInputProps {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step?: number;
  prefix?: string;
  suffix?: string;
}
function SliderInput({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  prefix = '',
  suffix = ''
}: SliderInputProps) {
  return <div className="flex flex-col gap-2" data-id="element-13">
      <div className="flex items-center justify-between" data-id="element-14">
        <label className="text-sm font-medium text-slate-600" data-id="element-15">{label}</label>
        <div className="flex items-center gap-1" data-id="element-16">
          {prefix && <span className="text-sm text-slate-400" data-id="element-17">{prefix}</span>}
          <input type="number" value={value} onChange={e => {
          const v = Number(e.target.value);
          if (!isNaN(v)) onChange(Math.max(min, Math.min(max, v)));
        }} className="w-20 text-right text-sm font-semibold text-slate-900 bg-white border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#6C2BD9]/30 focus:border-[#6C2BD9]" min={min} max={max} step={step} data-id="element-18" />
          {suffix && <span className="text-sm text-slate-400" data-id="element-19">{suffix}</span>}
        </div>
      </div>
      <input type="range" value={value} onChange={e => onChange(Number(e.target.value))} min={min} max={max} step={step} className="w-full h-1.5 bg-slate-200 rounded-full appearance-none cursor-pointer accent-[#6C2BD9] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#6C2BD9] [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-[#6C2BD9] [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer" data-id="element-20" />
    </div>;
}
export function CostCalculator() {
  const [products, setProducts] = useState(1);
  const [campaignsPerProduct, setCampaignsPerProduct] = useState(1);
  const [countries, setCountries] = useState(1);
  const [masterSnakTier, setMasterSnakTier] = useState<'none' | 'teaser' | 'trailer'>('none');
  const masterSnakCost = masterSnakTier === 'teaser' ? 2500 : masterSnakTier === 'trailer' ? 5000 : 0;
  const [campaignDuration, setCampaignDuration] = useState<3 | 6 | 12>(3);
  const MINIMUM_ORDER = 5000;
  // Localization cost scales with master snak tier (same % ratio)
  const localizationMultiplier = masterSnakTier === 'trailer' ? 2 : 1;
  const countryDeploymentCost = (countries >= 10 ? 800 : countries >= 5 ? 1000 : 1200) * localizationMultiplier;
  const monthlyInsightsFee = countries >= 10 ? 100 : countries >= 5 ? 125 : 150;
  const durationRebate = campaignDuration === 12 ? 0.1 : campaignDuration === 6 ? 0.05 : 0;
  const FOLLOW_UP_MASTER_COST = 1000;
  const calc = useMemo(() => {
    const totalCampaigns = products * campaignsPerProduct;
    const hasMaster = masterSnakCost > 0;
    // First campaign per brand = full master cost, subsequent = €1,500
    const firstCampaigns = hasMaster ? products : 0;
    const followUpCampaigns = hasMaster ? Math.max(0, totalCampaigns - products) : 0;
    const annualMasterSnakCost = hasMaster ? firstCampaigns * masterSnakCost + followUpCampaigns * FOLLOW_UP_MASTER_COST : 0;
    const deploymentPerCampaign = countryDeploymentCost * countries;
    const insightsPerCampaign = monthlyInsightsFee * campaignDuration * countries;
    // For first campaigns: full master + deployment + insights
    const rawCostFirstCampaign = masterSnakCost + deploymentPerCampaign + insightsPerCampaign;
    // For follow-ups: €1,500 + deployment + insights
    const rawCostFollowUp = FOLLOW_UP_MASTER_COST + deploymentPerCampaign + insightsPerCampaign;
    // For rollout-only: just deployment + insights
    const rawCostRollout = deploymentPerCampaign + insightsPerCampaign;
    const rawCostPerCampaign = hasMaster ? rawCostFirstCampaign : rawCostRollout;
    const rebateAmount = rawCostPerCampaign * durationRebate;
    const costAfterRebate = rawCostPerCampaign - rebateAmount;
    const belowMinimum = hasMaster && costAfterRebate > 0 && costAfterRebate < MINIMUM_ORDER;
    // Total annual: sum of first campaigns + follow-ups (each with rebate)
    const totalBeforeRebate = hasMaster ? firstCampaigns * rawCostFirstCampaign + followUpCampaigns * rawCostFollowUp : totalCampaigns * rawCostRollout;
    const totalAnnualCost = totalBeforeRebate * (1 - durationRebate);
    const costPerCampaign = totalCampaigns > 0 ? totalAnnualCost / totalCampaigns : 0;
    const annualDeploymentCost = totalCampaigns * deploymentPerCampaign;
    const annualInsightsCost = totalCampaigns * insightsPerCampaign;
    const totalRebate = totalCampaigns * rebateAmount;
    const totalDeployments = totalCampaigns * countries;
    const costPerDeployment = totalDeployments > 0 ? totalAnnualCost / totalDeployments : 0;
    return {
      totalCampaigns,
      totalDeployments,
      firstCampaigns,
      followUpCampaigns,
      rawCostPerCampaign,
      rawCostFollowUp,
      costAfterRebate,
      costPerCampaign,
      belowMinimum,
      rebateAmount,
      totalRebate,
      costPerDeployment,
      totalAnnualCost,
      annualMasterSnakCost,
      annualDeploymentCost,
      annualInsightsCost
    };
  }, [products, campaignsPerProduct, countries, masterSnakCost, countryDeploymentCost, monthlyInsightsFee, campaignDuration]);
  return <motion.div variants={containerVariants} initial="hidden" animate="show" className="w-full h-full overflow-y-auto" data-id="element-21">
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-8 md:py-12" data-id="element-22">
        {/* Header */}
        <motion.div variants={itemVariants} className="mb-8" data-id="element-23">
          <div className="flex items-center gap-3 mb-2" data-id="element-24">
            <span className="text-2xl font-bold tracking-tight text-[#6C2BD9]" data-id="element-25">
              Snakker
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight" data-id="element-26">
            Fast Marketing<sup className="text-[0.6em] font-medium" data-id="element-27">™</sup>{' '}
            Cost Calculator
          </h1>
          <p className="text-lg text-slate-500 mt-2" data-id="element-28">
            Estimate your annual campaign operating cost
          </p>
          <p className="text-sm text-slate-400 mt-1" data-id="element-29">
            Adjust the variables to see how your portfolio scales across brands,
            campaigns, countries, and insights.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12" data-id="element-30">
          {/* Inputs */}
          <motion.div variants={itemVariants} className="space-y-6" data-id="element-31">
            <div className="bg-[#6C2BD9]/[0.07] rounded-2xl p-6 space-y-6 border-2 border-[#6C2BD9]/20" data-id="element-32">
              <div className="mb-1" data-id="element-33">
                <h3 className="text-sm font-bold text-[#6C2BD9] uppercase tracking-widest" data-id="element-34">
                  Unit Economics
                </h3>
                <p className="text-xs text-[#6C2BD9]/60 mt-1" data-id="element-35">
                  Set your cost baseline first — everything else scales from
                  here.
                </p>
              </div>
              <div className="flex flex-col gap-2" data-id="element-36">
                <label className="text-sm font-medium text-slate-600" data-id="element-37">
                  Master Snak Type
                </label>
                <div className="grid grid-cols-3 gap-3" data-id="element-38">
                  <button onClick={() => setMasterSnakTier('none')} className={`relative rounded-xl border-2 p-4 text-left transition-all ${masterSnakTier === 'none' ? 'border-[#6C2BD9] bg-[#6C2BD9]/[0.06] shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'}`} data-id="element-39">
                    <p className={`text-sm font-semibold ${masterSnakTier === 'none' ? 'text-[#6C2BD9]' : 'text-slate-700'}`} data-id="element-40">
                      Rollout Only
                    </p>
                    <p className={`text-xs mt-1 ${masterSnakTier === 'none' ? 'text-[#6C2BD9]/70' : 'text-slate-400'}`} data-id="element-41">
                      Masters exist
                    </p>
                  </button>
                  <button onClick={() => setMasterSnakTier('teaser')} className={`relative rounded-xl border-2 p-4 text-left transition-all ${masterSnakTier === 'teaser' ? 'border-[#6C2BD9] bg-[#6C2BD9]/[0.06] shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'}`} data-id="element-42">
                    <p className={`text-sm font-semibold ${masterSnakTier === 'teaser' ? 'text-[#6C2BD9]' : 'text-slate-700'}`} data-id="element-43">
                      Teaser
                    </p>
                    <p className={`text-lg font-bold mt-1 ${masterSnakTier === 'teaser' ? 'text-[#6C2BD9]' : 'text-slate-900'}`} data-id="element-44">
                      €2,500
                    </p>
                  </button>
                  <button onClick={() => setMasterSnakTier('trailer')} className={`relative rounded-xl border-2 p-4 text-left transition-all ${masterSnakTier === 'trailer' ? 'border-[#6C2BD9] bg-[#6C2BD9]/[0.06] shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'}`} data-id="element-45">
                    <p className={`text-sm font-semibold ${masterSnakTier === 'trailer' ? 'text-[#6C2BD9]' : 'text-slate-700'}`} data-id="element-46">
                      Trailer
                    </p>
                    <p className={`text-lg font-bold mt-1 ${masterSnakTier === 'trailer' ? 'text-[#6C2BD9]' : 'text-slate-900'}`} data-id="element-47">
                      €5,000
                    </p>
                  </button>
                </div>
              </div>
              <div className="flex flex-col gap-2" data-id="element-48">
                <label className="text-sm font-medium text-slate-600" data-id="element-49">
                  Cost per Country Localization & Set-up
                </label>
                <div className="grid grid-cols-3 gap-2" data-id="element-50">
                  <div className={`rounded-lg border p-3 text-center ${countries >= 1 && countries < 5 ? 'border-[#6C2BD9] bg-[#6C2BD9]/[0.06]' : 'border-slate-200 bg-white'}`} data-id="element-51">
                    <p className={`text-xs font-medium ${countries >= 1 && countries < 5 ? 'text-[#6C2BD9]' : 'text-slate-400'}`} data-id="element-52">
                      1–4 countries
                    </p>
                    <p className={`text-lg font-bold mt-0.5 ${countries >= 1 && countries < 5 ? 'text-[#6C2BD9]' : 'text-slate-500'}`} data-id="element-53">
                      €{(1200 * localizationMultiplier).toLocaleString()}
                    </p>
                  </div>
                  <div className={`rounded-lg border p-3 text-center ${countries >= 5 && countries < 10 ? 'border-[#6C2BD9] bg-[#6C2BD9]/[0.06]' : 'border-slate-200 bg-white'}`} data-id="element-54">
                    <p className={`text-xs font-medium ${countries >= 5 && countries < 10 ? 'text-[#6C2BD9]' : 'text-slate-400'}`} data-id="element-55">
                      5–9 countries
                    </p>
                    <p className={`text-lg font-bold mt-0.5 ${countries >= 5 && countries < 10 ? 'text-[#6C2BD9]' : 'text-slate-500'}`} data-id="element-56">
                      €{(1000 * localizationMultiplier).toLocaleString()}
                    </p>
                  </div>
                  <div className={`rounded-lg border p-3 text-center ${countries >= 10 ? 'border-[#6C2BD9] bg-[#6C2BD9]/[0.06]' : 'border-slate-200 bg-white'}`} data-id="element-57">
                    <p className={`text-xs font-medium ${countries >= 10 ? 'text-[#6C2BD9]' : 'text-slate-400'}`} data-id="element-58">
                      10+ countries
                    </p>
                    <p className={`text-lg font-bold mt-0.5 ${countries >= 10 ? 'text-[#6C2BD9]' : 'text-slate-500'}`} data-id="element-59">
                      €{(800 * localizationMultiplier).toLocaleString()}
                    </p>
                  </div>
                </div>
                <p className="text-xs text-slate-400 mt-1" data-id="element-60">
                  Current rate:{' '}
                  <span className="font-semibold text-slate-600" data-id="element-61">
                    €{countryDeploymentCost.toLocaleString()}
                  </span>{' '}
                  per country
                  {masterSnakTier === 'trailer' ? ' (Trailer rate)' : ''} —
                  based on {countries}{' '}
                  {countries === 1 ? 'country' : 'countries'}
                </p>
              </div>
              <div className="flex flex-col gap-2" data-id="element-62">
                <label className="text-sm font-medium text-slate-600" data-id="element-63">
                  Monthly Hosting & Data Insights per Country
                </label>
                <div className="grid grid-cols-3 gap-2" data-id="element-64">
                  <div className={`rounded-lg border p-3 text-center ${countries >= 1 && countries < 5 ? 'border-[#6C2BD9] bg-[#6C2BD9]/[0.06]' : 'border-slate-200 bg-white'}`} data-id="element-65">
                    <p className={`text-xs font-medium ${countries >= 1 && countries < 5 ? 'text-[#6C2BD9]' : 'text-slate-400'}`} data-id="element-66">
                      1–4 countries
                    </p>
                    <p className={`text-lg font-bold mt-0.5 ${countries >= 1 && countries < 5 ? 'text-[#6C2BD9]' : 'text-slate-500'}`} data-id="element-67">
                      €150
                    </p>
                  </div>
                  <div className={`rounded-lg border p-3 text-center ${countries >= 5 && countries < 10 ? 'border-[#6C2BD9] bg-[#6C2BD9]/[0.06]' : 'border-slate-200 bg-white'}`} data-id="element-68">
                    <p className={`text-xs font-medium ${countries >= 5 && countries < 10 ? 'text-[#6C2BD9]' : 'text-slate-400'}`} data-id="element-69">
                      5–9 countries
                    </p>
                    <p className={`text-lg font-bold mt-0.5 ${countries >= 5 && countries < 10 ? 'text-[#6C2BD9]' : 'text-slate-500'}`} data-id="element-70">
                      €125
                    </p>
                  </div>
                  <div className={`rounded-lg border p-3 text-center ${countries >= 10 ? 'border-[#6C2BD9] bg-[#6C2BD9]/[0.06]' : 'border-slate-200 bg-white'}`} data-id="element-71">
                    <p className={`text-xs font-medium ${countries >= 10 ? 'text-[#6C2BD9]' : 'text-slate-400'}`} data-id="element-72">
                      10+ countries
                    </p>
                    <p className={`text-lg font-bold mt-0.5 ${countries >= 10 ? 'text-[#6C2BD9]' : 'text-slate-500'}`} data-id="element-73">
                      €100
                    </p>
                  </div>
                </div>
                <p className="text-xs text-slate-400 mt-1" data-id="element-74">
                  Current rate:{' '}
                  <span className="font-semibold text-slate-600" data-id="element-75">
                    €{monthlyInsightsFee}/mo
                  </span>{' '}
                  per country — based on {countries}{' '}
                  {countries === 1 ? 'country' : 'countries'}
                </p>
              </div>
              <div className="flex flex-col gap-2" data-id="element-76">
                <label className="text-sm font-medium text-slate-600" data-id="element-77">
                  Campaign Duration
                </label>
                <div className="grid grid-cols-3 gap-2" data-id="element-78">
                  <button onClick={() => setCampaignDuration(3)} className={`rounded-lg border-2 p-3 text-center transition-all ${campaignDuration === 3 ? 'border-[#6C2BD9] bg-[#6C2BD9]/[0.06]' : 'border-slate-200 bg-white hover:border-slate-300'}`} data-id="element-79">
                    <p className={`text-sm font-semibold ${campaignDuration === 3 ? 'text-[#6C2BD9]' : 'text-slate-700'}`} data-id="element-80">
                      3 months
                    </p>
                    <p className={`text-xs mt-1 ${campaignDuration === 3 ? 'text-[#6C2BD9]/70' : 'text-slate-400'}`} data-id="element-81">
                      Standard
                    </p>
                  </button>
                  <button onClick={() => setCampaignDuration(6)} className={`rounded-lg border-2 p-3 text-center transition-all ${campaignDuration === 6 ? 'border-[#6C2BD9] bg-[#6C2BD9]/[0.06]' : 'border-slate-200 bg-white hover:border-slate-300'}`} data-id="element-82">
                    <p className={`text-sm font-semibold ${campaignDuration === 6 ? 'text-[#6C2BD9]' : 'text-slate-700'}`} data-id="element-83">
                      6 months
                    </p>
                    <p className={`text-xs mt-1 ${campaignDuration === 6 ? 'text-[#6C2BD9]/70' : 'text-slate-400'}`} data-id="element-84">
                      5% rebate
                    </p>
                  </button>
                  <button onClick={() => setCampaignDuration(12)} className={`rounded-lg border-2 p-3 text-center transition-all ${campaignDuration === 12 ? 'border-[#6C2BD9] bg-[#6C2BD9]/[0.06]' : 'border-slate-200 bg-white hover:border-slate-300'}`} data-id="element-85">
                    <p className={`text-sm font-semibold ${campaignDuration === 12 ? 'text-[#6C2BD9]' : 'text-slate-700'}`} data-id="element-86">
                      12 months
                    </p>
                    <p className={`text-xs mt-1 ${campaignDuration === 12 ? 'text-[#6C2BD9]/70' : 'text-slate-400'}`} data-id="element-87">
                      10% rebate
                    </p>
                  </button>
                </div>
                {durationRebate > 0 && <p className="text-xs text-[#6C2BD9] mt-1 font-medium" data-id="element-88">
                    {Math.round(durationRebate * 100)}% duration rebate applied
                  </p>}
              </div>
            </div>

            <div className="bg-slate-50 rounded-2xl p-6 space-y-6 border border-slate-100" data-id="element-89">
              <div className="mb-1" data-id="element-90">
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-widest" data-id="element-91">
                  Portfolio Scale
                </h3>
                {masterSnakTier === 'none' && <p className="text-xs text-slate-400 mt-1" data-id="element-92">
                    Masters already created — calculating localization rollout
                    only.
                  </p>}
              </div>
              <SliderInput label="Number of Brands" value={products} onChange={setProducts} min={1} max={50} data-id="element-93" />
              <SliderInput label={masterSnakTier === 'none' ? 'Rollouts per Brand / Year' : 'Campaigns per Brand / Year'} value={campaignsPerProduct} onChange={setCampaignsPerProduct} min={1} max={6} data-id="element-94" />
              <SliderInput label="Number of Countries" value={countries} onChange={setCountries} min={1} max={120} data-id="element-95" />
            </div>
          </motion.div>

          {/* Output */}
          <motion.div variants={itemVariants} className="flex flex-col gap-6" data-id="element-96">
            {/* Main number */}
            <div className="bg-white rounded-2xl p-8 border-2 border-[#6C2BD9]/20 shadow-[0_0_40px_rgba(108,43,217,0.06)]" data-id="element-97">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-3" data-id="element-98">
                Estimated Annual Cost
              </p>
              <p className="text-4xl md:text-5xl font-bold text-[#6C2BD9] tracking-tight" data-id="element-99">
                {formatEuro(calc.totalAnnualCost)}
              </p>
              <p className="text-sm text-slate-400 mt-2" data-id="element-100">per year</p>
            </div>

            {/* Volume highlight */}
            <div className="bg-[#6C2BD9]/[0.04] rounded-2xl p-6 border border-[#6C2BD9]/10" data-id="element-101">
              <p className="text-xs font-semibold text-[#6C2BD9] uppercase tracking-widest mb-4" data-id="element-102">
                Campaign Volume
              </p>
              <div className="grid grid-cols-2 gap-4" data-id="element-103">
                <div data-id="element-104">
                  <p className="text-3xl font-bold text-slate-900" data-id="element-105">
                    {calc.totalCampaigns}
                  </p>
                  <p className="text-xs text-slate-500 mt-1" data-id="element-106">
                    master campaigns / year
                  </p>
                </div>
                <div data-id="element-107">
                  <p className="text-3xl font-bold text-[#6C2BD9]" data-id="element-108">
                    {calc.totalDeployments}
                  </p>
                  <p className="text-xs text-slate-500 mt-1" data-id="element-109">
                    localized deployments / year
                  </p>
                </div>
              </div>
              {calc.totalDeployments > 0 && <div className="mt-4 pt-4 border-t border-[#6C2BD9]/10 flex items-baseline gap-2" data-id="element-110">
                  <p className="text-2xl font-bold text-[#6C2BD9]" data-id="element-111">
                    {formatEuro(calc.costPerDeployment)}
                  </p>
                  <p className="text-sm text-slate-500" data-id="element-112">
                    per localized campaign
                  </p>
                </div>}
            </div>

            {/* Minimum order notice */}
            {calc.belowMinimum && <div className="bg-amber-50 rounded-2xl p-5 border border-amber-200" data-id="element-113">
                <p className="text-sm font-medium text-amber-800" data-id="element-114">
                  Minimum order per campaign: {formatEuro(MINIMUM_ORDER)}
                </p>
                <p className="text-xs text-amber-600 mt-1" data-id="element-115">
                  Your configured cost is {formatEuro(calc.rawCostPerCampaign)}{' '}
                  per campaign. Add more countries, extend duration, or increase
                  unit costs to reach the minimum.
                </p>
              </div>}

            {/* Breakdown */}
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100" data-id="element-116">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-4" data-id="element-117">
                Cost Breakdown
              </p>
              <div className="space-y-3" data-id="element-118">
                <div className="flex justify-between items-center" data-id="element-119">
                  <span className="text-sm text-slate-600" data-id="element-120">
                    Avg. cost per campaign
                  </span>
                  <span className="text-sm font-semibold text-slate-900" data-id="element-121">
                    {formatEuro(calc.costPerCampaign)}
                  </span>
                </div>
                <div className="h-px bg-slate-200 my-1" data-id="element-122" />
                {calc.firstCampaigns > 0 && <div className="flex justify-between items-center" data-id="element-123">
                    <span className="text-sm text-slate-500" data-id="element-124">
                      New masters ({calc.firstCampaigns} ×{' '}
                      {formatEuro(masterSnakCost)})
                    </span>
                    <span className="text-sm text-slate-700" data-id="element-125">
                      {formatEuro(calc.firstCampaigns * masterSnakCost)}
                    </span>
                  </div>}
                {calc.followUpCampaigns > 0 && <div className="flex justify-between items-center" data-id="element-126">
                    <span className="text-sm text-slate-500" data-id="element-127">
                      Follow-up campaigns ({calc.followUpCampaigns} × €1,500)
                    </span>
                    <span className="text-sm text-slate-700" data-id="element-128">
                      {formatEuro(calc.followUpCampaigns * FOLLOW_UP_MASTER_COST)}
                    </span>
                  </div>}
                <div className="flex justify-between items-center" data-id="element-129">
                  <span className="text-sm text-slate-500" data-id="element-130">
                    Localization & set-up
                  </span>
                  <span className="text-sm text-slate-700" data-id="element-131">
                    {formatEuro(calc.annualDeploymentCost)}
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-132">
                  <span className="text-sm text-slate-500" data-id="element-133">
                    Hosting & data insights ({campaignDuration}mo)
                  </span>
                  <span className="text-sm text-slate-700" data-id="element-134">
                    {formatEuro(calc.annualInsightsCost)}
                  </span>
                </div>
                {durationRebate > 0 && <div className="flex justify-between items-center" data-id="element-135">
                    <span className="text-sm text-[#6C2BD9] font-medium" data-id="element-136">
                      Duration rebate ({Math.round(durationRebate * 100)}%)
                    </span>
                    <span className="text-sm text-[#6C2BD9] font-medium" data-id="element-137">
                      −
                      {formatEuro(calc.totalAnnualCost / (1 - durationRebate) - calc.totalAnnualCost)}
                    </span>
                  </div>}
              </div>
            </div>

            {/* Strategic callout */}
            <div className="bg-[#6C2BD9]/5 rounded-2xl p-5 border border-[#6C2BD9]/10" data-id="element-138">
              <p className="text-sm font-medium text-[#6C2BD9] leading-relaxed" data-id="element-139">
                This is not software pricing. This is the annual cost of a fully
                managed campaign operating model across your portfolio.
              </p>
            </div>

            {/* Secondary message */}
            <p className="text-xs text-slate-400 leading-relaxed px-1" data-id="element-140">
              As you scale across more products, campaigns, or countries, cost
              grows predictably — while execution stays centralized.
            </p>
          </motion.div>
        </div>
      </div>
    </motion.div>;
}